<template>
  <section>
    <div class="container box mt-5">
      <h1 class="title">Declaración Horas Realizadas {{ year }}</h1>

      <div
        v-if="
          performanceInfo.state !== undefined &&
          performanceInfo.state !== null &&
          performanceInfo.state === 'DEVUELTA'
        "
        class="columns is-centered"
      >
        <div class="column is-10 is-centered">
          <b-message
            type="is-warning"
            has-icon
            title="Importante"
            aria-close-label="Close message"
          >
            Este compromiso de convenio de desempeño fue devuelto por el/la
            Director/a de departamento, con la siguiente observación:<br />
            <b
              ><span class="preserve-line-breaks">{{
                performanceInfo.comment
              }}</span></b
            >
          </b-message>
        </div>
      </div>

      <div class="columns mb-6 is-centered">
        <div class="column is-10 is-bordered-grey">
          <ConsolidatedFinal
            :academic_areas="academic_areas"
            :performances="performances"
            :areaWeights="areaWeights"
          />
        </div>
      </div>

      <b-tabs position="is-centered">
        <b-tab-item
          v-for="(academic_area, index) in academic_areas"
          :key="index"
          :label="academic_area.alt_name"
        >
          <AddCompletedHours
            :performances="performances"
            :academicArea="academic_area"
            @refreshPerformance="refresh"
            @addCommitment="addCommitment"
            :subjects="subjects"
            :subjectsSemester1="my_real_hours_subjects_semester_1"
            :subjectsSemester2="my_real_hours_subjects_semester_2"
            :isInPrecalification="isInPrecalification"
          />
        </b-tab-item>
      </b-tabs>

      <!-- Botón para enviar a comisión -->
      <div v-if="canSendToCommittee" class="columns mt-4">
        <div class="column has-text-centered">
          <b-message type="is-info" has-icon>
            Una vez que haya completado todas las evidencias, puede enviar su 
            desempeño a la comisión precalificadora.
          </b-message>
          
          <b-button
            label="Enviar a Comisión Precalificadora"
            type="is-success"
            icon-left="paper-plane"
            @click="openSendToCommitteeModal"
            size="is-medium"
          />
        </div>
      </div>

      <div class="columns">
        <div class="column has-text-centered">
          <b-button
            label="Ver Detalle"
            type="is-primary"
            icon-left="eye"
            tag="a"
            :href="`/performances/process/${year}/person/${person.id}`"
          />
        </div>
      </div>
    </div>

    <!-- Modal para enviar a comité -->
    <b-modal
      v-model="isSendToCommitteeModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      :can-cancel="['escape', 'outside']"
    >
      <template #default="props">
        <ModalSendToCommittee
          :key="`send-committee-${Date.now()}`"
          :performance-id="performanceInfo.id"
          :email="person.corporate_mail"
          @close="props.close"
          @open-verification-modal="openVerificationModal"
        />
      </template>
    </b-modal>

    <!-- Modal para confirmar con código -->
    <b-modal
      v-model="isConfirmModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      :can-cancel="['escape', 'outside']"
    >
      <template #default="props">
        <ModalConfirmSendToCommittee
          :performance-id="performanceInfo.id"
          :email="person.corporate_mail"
          @close="props.close"
        />
      </template>
    </b-modal>

    <b-loading :is-full-page="true" v-model="isLoading" :can-cancel="false" />
  </section>
</template>

<script>
import axios from "axios";
import AcademicConsolidated from "./AcademicConsolidated.vue";
import ConsolidatedFinal from "./ConsolidatedFinal.vue";
import AddCompletedHours from "./AddCompletedHours.vue";
import ModalSendToCommittee from "./ModalSendToCommittee.vue";
import ModalConfirmSendToCommittee from "./ModalConfirmSendToCommittee.vue";
import moment from "moment";
export default {
  name: "DeclarePerformanceHours",

  props: {
    canDeclare: {
      type: Boolean,
      required: true,
    },
    year: {
      type: Number,
      required: true,
    },
    person: {
      type: Object,
      required: true,
    },
  },

  components: {
    AcademicConsolidated,
    ConsolidatedFinal,
    AddCompletedHours,
    ModalSendToCommittee,
    ModalConfirmSendToCommittee,
  },

  data() {
    return {
      //Datos a traer
      academic_areas: [],
      performanceInfo: {},
      areaWeights: [],

      //Datos a guardar
      performances: [],

      //Auxiliares
      isLoading: false,
      subjects: [],
      my_real_hours_subjects_semester_1: [],
      my_real_hours_subjects_semester_2: [],
      
      // Modales
      isSendToCommitteeModalActive: false,
      isConfirmModalActive: false,
    };
  },

  computed: {
    canSendToCommittee() {
      // Validar que:
      // 1. El performance esté en estado ACEPTADA o DEVUELTA PRECALIFICACION
      // 2. La fase actual sea de Verificación (phase_d_type_id === 2)
      // 3. No esté ya en precalificación o precalificada
      // IMPORTANTE: Se incluye 'DEVUELTA PRECALIFICACION' como estado válido para reenvío
      const validStates = ['ACEPTADA', 'DEVUELTA PRECALIFICACION'];
      const invalidStates = ['EN PRECALIFICACION', 'PRECALIFICADA'];
      
      return (
        this.performanceInfo.state &&
        validStates.includes(this.performanceInfo.state) &&
        !invalidStates.includes(this.performanceInfo.state) &&
        this.performanceInfo.phase &&
        this.performanceInfo.phase.phase_d_type_id === 2
      );
    },
    
    isInPrecalification() {
      // IMPORTANTE: 'DEVUELTA PRECALIFICACION' NO se incluye aquí porque el académico 
      // debe poder editar y reenviar cuando el comité le devuelve el desempeño
      const precalificationStates = [
        'EN PRECALIFICACION',
        'PRECALIFICADA'
      ];
      return (
        this.performanceInfo.state &&
        precalificationStates.includes(this.performanceInfo.state)
      );
    },
  },

  async created() {
    if (!this.canDeclare) {
      this.$buefy.dialog.alert({
        title: "Error",
        message:
          "En estos momentos no se encuentra una fase activa para declarar, vuelva en otro momento.",
        type: "is-danger",
        hasIcon: true,
        iconPack: "fa",
        icon: "times-circle",
        confirmText: "Ok",
        onConfirm: () => {
          window.location.href = "/";
        },
      });
    }
    await this.getAcademicAreas();
    await this.getPerformanceInfoForAcademic();
    await this.getSubjects();
    await this.getMyRealHoursSubjects();
  },

  methods: {
    async getAcademicAreas() {
      this.isLoading = true;
      await axios
        .get("/academic_areas.json")
        .then((response) => {
          this.academic_areas = response.data;
        })
        .catch((error) => {
          this.$buefy.dialog.alert({
            title: "Error",
            message: "Hubo un error al obtener los datos. Intente más tarde.",
            type: "is-danger",
            hasIcon: true,
            iconPack: "fa",
            icon: "times-circle",
            confirmText: "Ok",
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async getPerformanceInfoForAcademic() {
      this.isLoading = true;
      await axios
        .get("/performances/get-performance-info-for-academic.json", {
          params: {
            person_id: this.person.id,
          },
        })
        .then((response) => {
          this.performanceInfo = response.data;

          const state_performance = this.performanceInfo.state;

          // Comentado: No redirigir si está ACEPTADA, permitir declarar horas y enviar a comité
          // if (
          //   state_performance === "EMITIDA" ||
          //   state_performance === "ACEPTADA"
          // ) {
          //   window.location.href = `/performances/process/${this.year}/person/${this.person.id}`;
          // }

          this.performanceInfo.phase.start_date = moment(
            this.performanceInfo.phase.start_date,
            ["YYYY-MM-DD", "DD-MM-YYYY"]
          );
          this.performanceInfo.phase.end_date = moment(
            this.performanceInfo.phase.end_date,
            ["YYYY-MM-DD", "DD-MM-YYYY"]
          );

          this.performances = this.performanceInfo.activity_performances
            ? this.performanceInfo.activity_performances
            : [];
          let index = 1;
          this.performances.forEach((performance) => {
            performance.commitment_index = index;
            index++;
          });

          // Obtener ponderaciones desde la respuesta
          this.areaWeights = this.performanceInfo.area_weights || [];
        })
        .catch((error) => {
          console.log(error);
          this.$buefy.dialog.alert({
            title: "Error",
            message: "Hubo un error al obtener los datos. Intente más tarde.",
            type: "is-danger",
            hasIcon: true,
            iconPack: "fa",
            icon: "times-circle",
            confirmText: "Ok",
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async getSubjects() {
      this.isLoading = true;
      axios
        .get("/performances/sai-subjects.json")
        .then((response) => {
          this.subjects = response.data;
          this.subjects.push({
            codigo: -1,
            electividad: -1,
            electivo: "N",
            nombre: "OTROS",
            sct: -1,
            tel: "0-0-0",
            tipo: "",
            titulacion: "",
            alt_nombre: "Otros",
            codigo_nombre: "OTROS",
          });
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    addCommitment(commitment) {
      this.performances.push(commitment);
      this.performances.sort(function (a, b) {
        return a.commitment_index - b.commitment_index;
      });
    },

    refreshPerfomance(commitment, isCommitmentDelete) {
      this.performances = this.performances.filter(
        (comm) => comm.commitment_index != commitment.commitment_index
      );
      if (isCommitmentDelete) {
        this.performances.forEach((performance, index) => {
          performance.commitment_index = index + 1;
        });
      }
    },

    async getMyRealHoursSubjects() {
      this.isLoading = true;
      axios
        .get(`/performances/real-hours-subjects/${this.year}.json`)
        .then((response) => {
          this.my_real_hours_subjects_semester_1 = response.data.semestre_1;
          this.my_real_hours_subjects_semester_2 = response.data.semestre_2;
          this.performances.forEach((performance) => {
            if (performance.subject_code) {
              const subject = this.my_real_hours_subjects_semester_1.find(
                (subj) => subj.codigo === parseInt(performance.subject_code)
              );
              const subject2 = this.my_real_hours_subjects_semester_2.find(
                (subj) => subj.codigo === parseInt(performance.subject_code)
              );
              if (subject || subject2) {
                performance.evidences.push({
                  id: -1,
                  name: "SAI",
                  alt_name: "Verificación horas por SAI",
                  subject_code: performance.subject_code,
                  created_at: "No Aplica",
                });
              }
            }
          });
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    refresh() {
      this.getPerformanceInfoForAcademic();
    },

    openSendToCommitteeModal() {
      this.isSendToCommitteeModalActive = true;
    },

    openVerificationModal(validationResult) {
      // Cerrar modal de validación y abrir modal de código
      this.isSendToCommitteeModalActive = false;
      this.isConfirmModalActive = true;
    },
  },
};
</script>

<style scoped>
.preserve-line-breaks {
  white-space: pre-line;
}
</style>
